// The in-editor user guide: every feature, how to reach it, and the
// non-obvious behaviour worth knowing, as plain data rendered (and
// searched) by components/HelpOverlay.tsx. Keep entries
// honest — when a feature has a limit (OCR is English-only, gradients
// must be locatable) the entry says so. The keyboard-shortcut table is
// NOT here: the overlay renders it live from the command registry so
// it can never drift.

export interface HelpEntry {
  title: string;
  /** Paragraphs. A line starting with "• " renders as a list item. */
  body: string[];
  /** Chip shown next to the title: a key ("E"), combo ("Ctrl+F") or
   *  gesture ("Shift+drag"). */
  kbd?: string;
}

export interface HelpSection {
  id: string;
  title: string;
  icon: string;
  blurb: string;
  entries: HelpEntry[];
}

export const HELP_SECTIONS: readonly HelpSection[] = [
  {
    id: "basics",
    title: "Getting started",
    icon: "ri-rocket-line",
    blurb:
      "Everything runs locally in your browser — the PDF engine (MuPDF) is compiled to WebAssembly and your files are never uploaded.",
    entries: [
      {
        title: "Opening documents",
        body: [
          "Use the Open button in the toolbar or drag & drop a PDF anywhere onto the page. Password-protected files prompt for their password and stay locked until you provide it.",
          "Each document opens in its own tab across the top. Double-click a tab to rename it; the name becomes the download filename.",
        ],
      },
      {
        title: "Your work is saved automatically",
        body: [
          "The session — open tabs, edits, annotations — persists in this browser (IndexedDB) and is restored when you come back. Nothing leaves your machine; clearing site data clears the session.",
          "For explicit restore points inside a session, see Checkpoints under “Saving & history”.",
        ],
      },
      {
        title: "Zoom & navigation",
        body: [
          "• Ctrl+scroll wheel zooms around the cursor; pinch works on trackpads.",
          "• The zoom control in the toolbar has presets plus Fit width (Ctrl+0) and Actual size (Ctrl+1); +/− keys also zoom.",
          "• Two-page (spread) view is in the command palette: “Toggle two-page view”.",
          "• The thumbnail rail on the left scrolls to any page; the page indicator in the status bar shows where you are.",
        ],
        kbd: "Ctrl+scroll",
      },
      {
        title: "Panning with the Hand tool",
        body: [
          "Zoomed in past the window, drag the page around instead of chasing scrollbars. The Hand tool (P) does nothing but pan — it can't select or edit anything, so you can drag from anywhere on the page.",
          "• Hold Space with any tool selected for a temporary hand — release it and you're back to what you were doing.",
          "• Middle-mouse drag pans from any tool, no key held.",
          "• The cursor tells you the mode: an open hand when panning is armed, a closed one while you drag.",
        ],
        kbd: "P / Space",
      },
      {
        title: "The side panel",
        body: [
          "The panel on the right holds the armed tool's options on top and, below it, whichever document panel you have open — objects, comments, bookmarks, fonts, page setup, headers, properties or accessibility.",
          "It stays where you put it. Switching to a tool with nothing to configure leaves the panel in place (it just says so) rather than collapsing and shifting the page sideways. Hide it with the button in the Options header, and bring it back from the same command or by opening any panel.",
        ],
      },
      {
        title: "The command palette",
        body: [
          "Every command in the editor — tools, panels, page operations, exports — is searchable from one box. If you can't find a button, the palette has it.",
        ],
        kbd: "Ctrl+Shift+P",
      },
      {
        title: "Undo covers everything",
        body: [
          "Annotations, content edits, page operations (delete, reorder, rotate, crop), form fields — all of it is undoable with Ctrl+Z and redoable with Ctrl+Shift+Z or Ctrl+Y.",
        ],
        kbd: "Ctrl+Z",
      },
    ],
  },
  {
    id: "annotate",
    title: "Annotate & mark up",
    icon: "ri-mark-pen-line",
    blurb:
      "These tools add real PDF annotations — they stay editable in Acrobat and other viewers after download.",
    entries: [
      {
        title: "Text boxes",
        body: [
          "Press T and click (or drag a box) to add free text. The Options panel offers the typeface, bold and italic, size, colour, alignment and opacity. Drag to move, use the corner handles to resize; double-click to edit later.",
          "Typefaces: the three PDF standard faces (Helvetica, Times, Courier) always work and add nothing to the file size; the library faces and anything you add are embedded into the PDF on export. Add your own with the upload button (TTF/OTF) or fetch any family from the Google Fonts catalogue by name — added faces last for the session.",
          "Chinese, Japanese and Korean can be typed straight into a text box. Kana and hangul identify themselves, but Han characters are shared by all four scripts, so a script picker appears to say which one to draw them with — it only matters for text that is Han and nothing else. CJK is written with the standard PDF CJK fonts, which readers substitute: about a kilobyte per script rather than a 30 MB embedded face. Lines wrap between characters (Chinese and Japanese have no spaces to break at) and never start with a closing bracket or full stop.",
        ],
        kbd: "T",
      },
      {
        title: "Text markup: highlight, underline, strikeout, squiggly",
        body: [
          "Pick the tool (H / U / S / Q) and drag across existing text — the markup snaps to the characters you cover. Colour is adjustable from the selection frame afterwards.",
        ],
        kbd: "H",
      },
      {
        title: "Freehand drawing",
        body: [
          "Press D and draw. Stroke colour and width are in the tool options; each stroke becomes its own annotation you can move, recolour or delete.",
        ],
        kbd: "D",
      },
      {
        title: "Shapes: rectangle, ellipse, line, arrow",
        body: [
          "The shape menu in the toolbar (or keys R / O / L / A) draws outlined or filled shapes. Lines and arrows offer end-cap styles; all shapes support stroke/fill colour, width and dash patterns from the selection frame.",
        ],
        kbd: "R",
      },
      {
        title: "Sticky notes",
        body: [
          "Press N and click to drop a note icon with a pop-up comment. Notes carry an author and timestamp and appear in the Comments panel, where they can be replied to and resolved.",
        ],
        kbd: "N",
      },
      {
        title: "Stamps",
        body: [
          "The stamp button opens a library of standard stamps (APPROVED, NOT APPROVED, drafts and more). Click one, then click the page to place it.",
        ],
      },
      {
        title: "Signatures",
        body: [
          "“Add signature…” (toolbar or palette) opens a dialog with three tabs: Draw with the mouse, Type your name in a script font, or Upload an image. The result is placed like a stamp; your last signature is remembered for reuse.",
          "This is a picture of a signature. It shows intent, and it proves nothing about who made it or whether the document changed afterwards — for that, see “Digitally sign” under Saving & export.",
        ],
      },
      {
        title: "Images",
        body: [
          "“Insert image…” places a PNG/JPEG/WebP onto the page. You can also paste an image straight from the OS clipboard (e.g. a screenshot) with Ctrl+V.",
        ],
        kbd: "Ctrl+V",
      },
      {
        title: "Links",
        body: [
          "Press K and drag a clickable area, then choose a web URL or a page in this document to jump to. Existing links are followed with the Select tool and edited with the Link tool.",
        ],
        kbd: "K",
      },
      {
        title: "Whiteout",
        body: [
          "Press W and drag to cover content with an opaque white rectangle. Note: whiteout only covers — the content underneath is still in the file. To truly remove it, use Redact (see “Redaction & security”).",
        ],
        kbd: "W",
      },
      {
        title: "Background blocks",
        body: [
          "The Block tool draws a coloured rectangle BEHIND the page content — useful for tinted backdrops behind existing text. Press an existing block with the Select tool to recolour or move it, like any other page-content fill.",
        ],
      },
      {
        title: "Measure",
        body: [
          "Press M and drag between two points to read a distance. Pick units (pt, mm, cm, in) or calibrate against a known length — e.g. drag along a scale bar and type its real value. Measurements are on-screen aids and are not exported.",
          "The unit and calibration you set here also label every resize: drawing or resizing a shape, image, colour block or stamp shows its width and height (a line or arrow shows its length), and a marquee shows the size of the region. The labels appear while you drag and disappear when you let go.",
        ],
        kbd: "M",
      },
    ],
  },
  {
    id: "content",
    title: "Edit page content",
    icon: "ri-edit-line",
    blurb:
      "The Select tool (V) changes the page itself as well as the overlay — press the original text, images, colour blocks or gradients to select them, and press again to edit.",
    entries: [
      {
        title: "Edit existing text",
        body: [
          "Click a line of text to rewrite it in place, or Shift+click to take the whole paragraph at once. The formatting bar lets you restyle: font family (including the built-in font library and your own uploaded fonts), size, bold/italic and colour.",
          "Selected text objects can also be moved (drag the grip) and scaled (corner knob).",
          "Limits: typing is limited to Latin-1 characters (no CJK yet), and line spacing within a paragraph is preserved as-is.",
        ],
        kbd: "V",
      },
      {
        title: "Find & replace",
        body: [
          "Open search (Ctrl+F) and switch to Replace mode to substitute the current match or every match in the document — replacements re-typeset the affected line.",
        ],
        kbd: "Ctrl+F",
      },
      {
        title: "Edit images",
        body: [
          "Click an image to select it, then move, resize, replace (swap in a new file) or delete it from its frame.",
        ],
        kbd: "V",
      },
      {
        title: "Edit colour fills & blocks",
        body: [
          "Blocks and images resize from any of their four corners, and the opposite corner stays pinned — so a block whose top edge is in the wrong place can be fixed from the top edge. Images keep their aspect ratio; blocks resize freely. The size shows in the Measure tool's units while you drag.",
          "Click a solid colour area to recolour, move or delete it, or restack it — “Bring to front” paints it above everything on the page (text included), “Send to back” puts it behind all other content.",
          "Fills clipped to a non-rectangular shape are delete-only.",
        ],
        kbd: "V",
      },
      {
        title: "Edit gradients",
        body: [
          "Click a gradient to select it. You can recolour its start and end stops, reverse its direction, or delete it. Gradients painted through patterns or nested art may be view-only (the frame will say so).",
        ],
        kbd: "V",
      },
      {
        title: "Move several things at once (region select)",
        body: [
          "Drag from an empty spot (a margin, a gap between paragraphs) to marquee-select everything in the region — text, images, fills, form fields and annotations together — then drag the group to a new position. Dragging out of a word selects that text instead, so one gesture covers both. Ctrl/Cmd+drag extends an existing selection rather than replacing it.",
          "To build a selection by hand, Ctrl+click (Cmd+click on a Mac) each object — text line, image, colour block, form field or annotation. Once anything is selected, plain Shift+click adds to it as well; with nothing selected Shift+click still opens a whole paragraph for editing. Clicking an object that's already in the selection removes it again.",
          "The group then moves, nudges, aligns and deletes as one, in a single undo step. Objects have to be on the same page — picking one on another page starts a new selection.",
        ],
        kbd: "Drag / Ctrl+click",
      },
      {
        title: "How content editing works (and what to expect)",
        body: [
          "Edits erase the original object and redraw the replacement, so the change is real — the old bytes are gone from the visible page. Fonts are re-embedded as needed; exotic colour spaces are recoloured as RGB; dashed borders on redrawn shapes come back solid.",
        ],
      },
    ],
  },
  {
    id: "precision",
    title: "Selection & precision",
    icon: "ri-focus-3-line",
    blurb: "Working with annotations using the Select tool (V).",
    entries: [
      {
        title: "Multi-select",
        body: [
          "• Shift+click adds the object under the pointer to the selection — annotation, form field or page content alike.",
          "• Shift+drag on empty page draws a marquee, which sweeps up everything it covers.",
          "• Ctrl+A selects every annotation on the current page.",
          "A multi-selection moves as a group, nudges with the arrow keys, aligns and distributes, and deletes in one go — all as a single undo step, however many kinds of object are in it.",
          "Mixed selections live on ONE page: picking an object on another page starts a new selection. Page content and form fields are rewritten in the document when they move, so those edits take a moment; annotations move instantly.",
        ],
        kbd: "Shift+click",
      },
      {
        title: "Align & distribute",
        body: [
          "With two or more objects selected, the toolbar offers align left/centre/right and top/middle/bottom, plus horizontal and vertical distribution with equal gaps. It works on any mix — annotations, page content and form fields.",
        ],
      },
      {
        title: "Snapping & guides",
        body: [
          "While dragging, red guide lines appear when edges or centres line up with other annotations, page content or the page centre — the drag snaps to them. Hold the drag steady to see exact alignment.",
        ],
      },
      {
        title: "Rotate",
        body: [
          "Drag the grip above a selection to turn it, or use Ctrl+] and Ctrl+[ for 90° steps. Hold Shift while dragging to snap to 15°. Works on one annotation or a whole group — a group turns about its shared centre, so it keeps its arrangement.",
          "Highlights, drawings, lines and arrows rotate their actual geometry, so they stay ordinary annotations any reader draws correctly. Text boxes, stamps, rectangles and ellipses are stored as upright rectangles in PDF, so their rotation lives in the appearance — which is the same mechanism Acrobat uses for a turned stamp. Notes and links don't rotate: a note icon has no orientation, and a link has to stay square to stay clickable.",
        ],
        kbd: "Ctrl+]",
      },
      {
        title: "Nudge",
        body: [
          "Arrow keys move the selection 1 pt; Shift+arrows move 10 pt. Repeated nudges merge into a single undo step.",
        ],
        kbd: "↑↓←→",
      },
      {
        title: "Clipboard",
        body: [
          "Ctrl+C / Ctrl+X / Ctrl+V copy, cut and paste annotations — including across tabs. Ctrl+D duplicates in place with a small offset. Pasting an OS image (screenshot) inserts it as an image annotation; pasting plain text creates a text box.",
        ],
        kbd: "Ctrl+C",
      },
      {
        title: "Right-click context menu",
        body: [
          "Right-clicking a selection (or the page) offers the common actions in place: copy, paste, duplicate, delete, z-order and tool-specific extras.",
        ],
      },
      {
        title: "Select & copy document text",
        body: [
          "With the Select tool, drag across page text to select it; Ctrl+C copies the plain text.",
        ],
        kbd: "V",
      },
      {
        title: "Keyboard access",
        body: [
          "Tab moves focus through the annotations on the page; Enter selects the focused one, Delete removes it. All dialogs and panels are reachable by keyboard.",
        ],
        kbd: "Tab",
      },
    ],
  },
  {
    id: "pages",
    title: "Pages",
    icon: "ri-file-copy-2-line",
    blurb: "The thumbnail rail on the left is page mission control.",
    entries: [
      {
        title: "Reorder, rotate, delete",
        body: [
          "Drag thumbnails to reorder pages. Each thumbnail's menu rotates or deletes that page; Ctrl+click / Shift+click selects several pages for batch rotate, delete or extract.",
        ],
      },
      {
        title: "Extract pages",
        body: [
          "Select pages in the rail and choose Extract to download just those pages as a new PDF (the open document is untouched).",
        ],
      },
      {
        title: "Insert pages",
        body: [
          "“Add blank page” inserts an empty page after the current one. “Insert pages from another PDF…” previews the source document and lets you pick a page range and insertion point.",
        ],
      },
      {
        title: "Import Markdown as PDF",
        body: [
          "The Open menu's “Markdown as styled PDF…” (or “Import MD” in the rail, or dropping a .md file onto the editor) turns a Markdown file into a styled PDF document. Pick one of the pre-styled themes, then customise any structure the format supports — body text, each heading level, code blocks, inline code, quotes, lists, tables, links, dividers, images and the page itself (size, margins, background).",
          "The preview shows the actual generated PDF, page for page — importing locks in exactly what you see as a new tab. Headings become PDF bookmarks, links stay clickable (including #heading anchors), and your theme customisations are remembered for the next import.",
          "• Fonts: built-in Helvetica/Times/Courier, the font library, and faces you've uploaded in this session.",
          "• Images: data-URL and downloadable https images are embedded; anything unreachable renders as a labelled placeholder.",
          "• Text is Latin-script (WinAnsi) for now — CJK and emoji print as “?”.",
        ],
      },
      {
        title: "Import a Word document or HTML as PDF",
        body: [
          "The Open menu's “Word document…” (or “Import Word” in the rail, or dropping a .docx onto the editor) converts a Word file into a styled PDF. “HTML file…” does the same for saved .html/.htm pages.",
          "Both land in the same theme-and-preview screen as a Markdown import: the document's structure — headings, bold and italic, lists, tables, quotes, links and embedded images — is converted, and you choose how it looks from the theme gallery, restyling anything before you confirm. Headings become PDF bookmarks and links stay clickable.",
          "This re-flows the document rather than reproducing Word's exact page layout — a browser has no copy of Word's layout engine, so instead of an unreliable imitation you get clean structure you can style. Page-specific furniture (Word headers/footers, precise positioning, text boxes, columns) is not carried over; add it afterwards with the Headers, footers & watermark panel.",
          "• Any styles the converter could not interpret are counted in the header as “conversion notes” — hover to read them.",
          "• Legacy .doc files are not supported; re-save them as .docx first.",
        ],
      },
      {
        title: "Turn images into a PDF",
        body: [
          "The Open menu's “Images as PDF…” (or “Images to PDF” in the rail, or dropping image files onto the editor) builds a PDF from JPG, PNG, WebP, AVIF, HEIC, GIF, BMP or SVG files — pick several at once.",
          "Choose a layout preset (Full page, Edge to edge, Contact sheet, Album, Polaroid, Proof sheet), then set the grid (columns × rows), how each image fits its cell (whole image, fill-and-crop, or frame-hugs-image), gaps, borders, mats, shadows, captions (file name or number) and page headers/footers. Drag the thumbnails in the sidebar to reorder, or remove one with its ✕.",
          "• At one image per page, each page can follow its own image's orientation — landscape photos get landscape pages.",
          "• JPEG and PNG files are embedded at full resolution with no recompression; other formats are converted first, as are JPEGs that carry a rotation flag.",
        ],
      },
      {
        title: "Import a spreadsheet as PDF",
        body: [
          "The Open menu's “Spreadsheet as PDF table…” (or “Import sheet” in the rail, or dropping the file onto the editor) turns CSV, TSV, Excel (XLSX/XLSM/XLSB/XLS), OpenDocument (ODS/FODS), DIF, SYLK, PRN or DBF data into a styled PDF table.",
          "Pick a theme, then tune columns (width mode, alignment), rows (stripes, blank-row handling), cells (font, padding, wrap vs clip, grid lines), the header row (styling, repeat on every page) and running page headers/footers with {page} {pages} {sheet} {file} {date} placeholders. The preview is the generated PDF itself.",
          "• Multi-sheet workbooks: choose which sheets to include — each starts a new page and becomes a PDF bookmark.",
          "• Cell values keep the source file's number formatting (dates, currencies, percentages); numbers auto-align right.",
          "• Very large sheets are trimmed to 10,000 rows × 100 columns per sheet.",
        ],
      },
      {
        title: "Crop",
        body: [
          "Press C and drag the area to keep. Cropping is non-destructive — it trims the page box without deleting content, and can be removed later from Page setup.",
        ],
        kbd: "C",
      },
      {
        title: "Page setup",
        body: [
          "The Page setup panel changes page size (with content scaling options) and margins for one page or the whole document, and removes crops.",
        ],
      },
      {
        title: "Headers, footers & watermark",
        body: [
          "The “Headers, footers & watermark…” panel stamps text in the margins or diagonally across pages. Tokens {page}, {pages} and {date} are substituted per page.",
        ],
      },
    ],
  },
  {
    id: "review",
    title: "Comments & review",
    icon: "ri-chat-3-line",
    blurb: "A review workflow over standard PDF annotations.",
    entries: [
      {
        title: "The Comments panel",
        body: [
          "Lists every annotation with a comment, grouped by page. Click one to jump to it. Set your author name once and it is stamped on everything you add.",
        ],
      },
      {
        title: "Replies & status",
        body: [
          "Each comment supports threaded replies and a review status (accepted, rejected, completed, cancelled). These are standard PDF review fields — they round-trip with Acrobat.",
        ],
      },
      {
        title: "Copy a summary",
        body: [
          "The panel's “copy summary” button puts a plain-text digest of all comments on the clipboard — handy for a review email.",
        ],
      },
      {
        title: "Share comments as XFDF",
        body: [
          "“Export comments (XFDF)…” writes every markup, note, drawing and shape to a small .xfdf file — the standard Adobe interchange format. The document itself does not travel with it, so two people can mark up the same PDF separately and merge the results.",
          "“Import XFDF…” reads one back in, replies and review status included, as a single undo step. Comments naming a page this document doesn't have are reported rather than dropped onto the wrong page.",
          "Stamps, signatures and pending redactions stay behind: XFDF carries a stamp only as an embedded appearance stream, and a redaction is an instruction to remove content, not a comment. The toast says what was left out.",
        ],
      },
      {
        title: "Compare two PDFs",
        body: [
          "“Compare with another PDF…” opens a full-screen side-by-side view with synchronised scrolling and zoom, plus a pixel-difference overlay that highlights what changed between versions.",
        ],
      },
    ],
  },
  {
    id: "forms",
    title: "Forms",
    icon: "ri-input-field",
    blurb: "Fill existing forms or build new ones.",
    entries: [
      {
        title: "Filling forms",
        body: [
          "Existing AcroForm fields (text, checkboxes, radio buttons, dropdowns) are live — click and type with the Select tool. Values are saved into the real form fields on download.",
        ],
      },
      {
        title: "Moving and resizing fields",
        body: [
          "With the Field tool armed (F), existing fields become boxes you can drag and resize from any corner, each labelled with its name. Every other tool leaves them as ordinary form controls, so you can never shove a field sideways while filling it in. Each move is one undo step.",
        ],
        kbd: "F",
      },
      {
        title: "Creating fields",
        body: [
          "Press F and drag a box, then choose the field type — text field, checkbox, dropdown, radio button or signature field — and set its name and options.",
          "Radio buttons: each box you drag is one button, and boxes with the same group name form one group — picking any button clears the others, in this editor and in every reader. Give each button its own choice value (“Standard”, “Express”…).",
          "A signature field is a designated place to sign: it shows as a dashed placeholder here and as a click-to-sign target in Acrobat and other readers. To cryptographically sign the whole document from this editor, use File → “Digitally sign…”.",
        ],
        kbd: "F",
      },
      {
        title: "Form data in and out (XFDF)",
        body: [
          "“Export form data (XFDF)…” writes the answers on their own, without the form — the format Acrobat and most server-side tooling read. Every field is written, not just the ones you touched, so the file describes the whole form.",
          "“Import XFDF…” fills them back in. It is one menu item for both halves of the format: the file decides whether it carries answers, comments or both, and the toast reports what matched.",
        ],
      },
      {
        title: "Flattening",
        body: [
          "The download menu can flatten form fields (bake the filled values into the page so they are no longer editable) or flatten everything, annotations included.",
        ],
      },
    ],
  },
  {
    id: "accessibility",
    title: "Accessibility",
    icon: "ri-wheelchair-line",
    blurb: "What a screen reader can make of the document.",
    entries: [
      {
        title: "The accessibility check",
        body: [
          "The Accessibility panel runs about ten checks over the open document — whether a screen reader is allowed to read it at all, whether the text is really text, whether the language, title and form-field descriptions are set — and says which are failing and why.",
          "It only ever rules problems out. A document that passes every check can still be unreadable: whether the alternate text is right, or the reading order makes sense, is something only a person can tell you, and the panel says so rather than awarding a grade.",
        ],
      },
      {
        title: "One-click fixes",
        body: [
          "Checks that come down to a single flag in the file get a Fix button: the document language, showing the title instead of the file name in the window bar, giving each page of form fields a tab order, and seeding each field's description from its own name. Each one is a normal, undoable document edit.",
          "A seeded field description is a starting point — “billing.postal_code” becomes “Postal code”. Reword the ones that read badly.",
        ],
      },
      {
        title: "Documents this editor creates are tagged",
        body: [
          "Anything imported as a PDF here — Markdown, a Word document, HTML, a spreadsheet or a set of images — comes out with a real structure tree: headings as headings, paragraphs as paragraphs, lists as lists, table header cells distinguished from data cells, and each picture as a figure carrying whatever alt text the source gave it. Rules, borders and background tints are marked as decoration so a reader skips them instead of reading them aloud.",
          "That is possible because the editor writes those pages itself and so knows what every line of them is. It is NOT possible for a PDF that arrives already made: the information simply isn't in the page, which is why the check reports an untagged file rather than offering to fix one.",
        ],
      },
      {
        title: "What has no Fix button, and why",
        body: [
          "Tagging an ALREADY-MADE PDF, and writing alternate text, are content decisions rather than flags. A button that invented a structure tree would produce a file that passes checkers and reads as nonsense, so there isn't one — the check explains what is missing and leaves it to you. (Documents the editor generates are tagged as they are written; see above.)",
          "Checks answered elsewhere in the editor link across instead: a scanned page offers OCR, an encryption ban on text extraction opens Security, and undescribed annotations open the Comments panel.",
        ],
      },
      {
        title: "Describing an annotation",
        body: [
          "A highlight, drawing or shape has no words of its own, so a screen reader can only announce its comment. Open a comment thread in the Comments panel and fill in “Comment — also what a screen reader announces”; it is written as the annotation's /Contents on download and travels in XFDF exports.",
        ],
      },
    ],
  },
  {
    id: "security",
    title: "Redaction & security",
    icon: "ri-shield-keyhole-line",
    blurb: "Removing content for real, and locking the file.",
    entries: [
      {
        title: "Redact — true removal",
        body: [
          "Press X and drag over what must go. Redactions apply at download: the text, image data and vector art under each box are genuinely deleted from the file — not hidden — and replaced by a black box. Until download they show as marks you can still adjust or remove.",
          "This is the difference from Whiteout, which only paints over content while leaving it extractable underneath.",
        ],
        kbd: "X",
      },
      {
        title: "Find & redact — scan the whole document",
        body: [
          "“Find & redact” (left rail, or the command palette) searches every page for a phrase you type and for classes of sensitive data: email addresses, phone numbers, payment card numbers, tax file numbers and ABNs, US social security numbers, IP addresses and web addresses. You can also supply your own regular expression.",
          "Card numbers, TFNs and ABNs are checked against their real checksums, and a run of digits sitting inside a longer number is ignored — so an order reference doesn't get mistaken for a phone number.",
          "Results are listed per page with the surrounding line for context; click one to jump to it, untick anything you want to keep, then “Mark for redaction”. That adds ordinary redaction marks in a single undoable step — nothing is destroyed until you download.",
          "Two limits worth knowing: a match must sit on one line (text broken across a line break isn't found), and a scanned page has no text until you run OCR.",
        ],
      },
      {
        title: "Password protection & permissions",
        body: [
          "“Security & permissions…” (download menu, or the Properties panel) sets what happens to every export in this session: keep the original protection, remove it, or encrypt with AES-256.",
          "Two passwords, either or both: a password to open the file, and a permissions password that controls what viewers may do — printing, high-resolution printing, copying, changing, commenting, filling forms, assembling pages and accessibility extraction. Leaving the open password blank makes a file anyone can read but not, say, print.",
          "Restrictions are a request that standards-compliant viewers honour; the open password is the part that actually protects the file, and it cannot be recovered. The copy in the editor is never encrypted.",
        ],
      },
      {
        title: "Removing a password",
        body: [
          "Open the protected file (you'll be prompted for its password), then choose “Remove protection” in Security & permissions. The next download opens without a password.",
        ],
      },
      {
        title: "Remove metadata",
        body: [
          "The download menu's “Remove metadata” toggle strips the document Info dictionary and XMP metadata (author, title, creation tool…) from every export in this session. To change those values instead of deleting them, use the Properties panel.",
        ],
      },
      {
        title: "Opening encrypted files",
        body: [
          "Password-protected PDFs prompt on open. The password is kept only for this browser session so the tab can restore itself; it is never stored in the file or sent anywhere.",
        ],
      },
    ],
  },
  {
    id: "doctools",
    title: "Document tools",
    icon: "ri-tools-line",
    blurb: "Search, OCR, structure and inspection.",
    entries: [
      {
        title: "Search",
        body: [
          "Ctrl+F searches the document with live highlights; Enter / Shift+Enter step through matches. Switch to Replace mode for find & replace.",
        ],
        kbd: "Ctrl+F",
      },
      {
        title: "OCR — recognise scanned text",
        body: [
          "“Recognise text (OCR)…” runs Tesseract (locally, like everything else) over scanned pages and adds an invisible text layer, making the document searchable and copyable. Run it on all pages or just the current one; pages that already contain text are skipped by default. English is the bundled language.",
        ],
      },
      {
        title: "Bookmarks (outline)",
        body: [
          "The Bookmarks panel shows the document outline for navigation and lets you add, rename, re-nest and delete entries — the edited outline is saved into the PDF.",
        ],
      },
      {
        title: "Properties panel",
        body: [
          "Title, author, subject, keywords, creator and producer — the fields a reader shows in File → Properties. Editing them writes both the Info dictionary and the XMP packet, so metadata readers and Acrobat agree, and the change is undoable like any other edit. The panel also reports the page count, PDF version, dates, and what the file's own encryption allows.",
        ],
      },
      {
        title: "Fonts panel",
        body: [
          "Lists every font embedded in the document, where it is used, and its embedding status.",
        ],
      },
      {
        title: "Objects panel",
        body: [
          "An inventory of the page's content — text blocks, images, fills — with click-to-select, as an alternative to hunting for a small object on the page.",
        ],
      },
      {
        title: "Inspector",
        body: [
          "The inspector toggle outlines the structure under the cursor — text blocks, lines, images, form fields and annotations — useful for understanding what a press will pick.",
        ],
      },
    ],
  },
  {
    id: "output",
    title: "Saving, export & history",
    icon: "ri-download-2-line",
    blurb: "Getting your work out, and going back in time.",
    entries: [
      {
        title: "Download",
        body: [
          "File ▸ Download PDF saves the edited document with real, editable annotations. The same menu holds the variants: flattened form fields, flatten everything, security & permissions, the remove-metadata toggle — and Print.",
        ],
      },
      {
        title: "Print",
        body: [
          "Ctrl+P prints the edited document (not the app around it) via the browser's print dialog.",
        ],
        kbd: "Ctrl+P",
      },
      {
        title: "Digitally sign with a certificate",
        body: [
          "File → “Digitally sign…” signs the exported file with your own certificate, so any reader can tell whether a single byte has changed since. Take a .p12 / .pfx file (what a certificate authority issues) and its password, or paste a PEM certificate and unencrypted PKCS#8 key.",
          "Your private key never leaves the page. The document is prepared here, the digest is taken over the file's own byte ranges, and the signature is assembled in the browser — nothing is uploaded, and no server ever sees the key.",
          "The signature is appended as an incremental update, which leaves the original bytes intact underneath: that is what lets a reader show exactly what was signed. You can add an optional visible stamp in any corner of a page, or sign invisibly.",
          "It downloads rather than replacing the open document, on purpose: editing a signed file invalidates its signature, so signing is the last step. A .p12 using the old Triple-DES or RC2 encryption can't be read in a browser — the dialog says so and gives the openssl command to re-export it with AES.",
        ],
      },
      {
        title: "Checkpoints",
        body: [
          "The checkpoints menu keeps restore points for the current tab: one is added automatically on every download, and “Checkpoint now” adds one on demand. Restoring rewinds the document to that state — and the restore itself is undoable.",
        ],
      },
      {
        title: "Extract & split",
        body: [
          "Select pages in the thumbnail rail and Extract to save them as a separate PDF — the quickest way to split a document.",
        ],
      },
    ],
  },
];

/** Mouse/touch gestures that aren't commands — appended to the
 *  shortcut table the overlay builds from the command registry. */
export const HELP_GESTURES: readonly { keys: string; does: string }[] = [
  { keys: "Ctrl+Scroll", does: "Zoom at the cursor" },
  { keys: "Shift+Click", does: "Edit a whole paragraph instead of one line" },
  { keys: "Shift+Drag", does: "Marquee-select everything it covers (Select tool)" },
  { keys: "Drag from empty space", does: "Marquee-select everything in the region" },
  { keys: "Ctrl/Cmd+Click", does: "Add or remove one object from the selection" },
  { keys: "Right-click", does: "Context menu on selection or page" },
  { keys: "Double-click tab", does: "Rename the document" },
  { keys: "Ctrl+Click thumbnail", does: "Toggle page in batch selection" },
  { keys: "Shift+Click thumbnail", does: "Select a page range" },
];
